import type { SeoPage, SeoSection } from "./seo-engine";
import { getPropertyDistrict } from "./seo-cta";
import { translate, formatMad } from "./i18n";
import { DEFAULT_LOCALE, type Locale } from "./locale";

function findSection(page: SeoPage, type: string): SeoSection | undefined {
  return page.sections?.find((s) => s.type === type);
}

function pickNum(rec: Record<string, unknown> | null | undefined, ...keys: string[]): number | null {
  if (!rec) return null;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function pickStr(rec: Record<string, unknown> | null | undefined, ...keys: string[]): string | null {
  if (!rec) return null;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

function localizedEnum(locale: Locale, namespace: string, raw: string | null): string | null {
  if (!raw) return null;
  const key = raw.toLowerCase().trim();
  if (key === "unclear") return null;
  const v = translate(locale, `${namespace}.${key}`);
  if (v === `${namespace}.${key}`) return raw.replace(/_/g, " ").trim();
  return v;
}

/**
 * Compose a search-optimised H1 for a property page in the active locale.
 *
 * Format: "[sqm]m² Riad for sale in [District], Marrakech — [Positioning][, BestUse]"
 * Example: "200m² Riad for sale in Arset El Maach, Marrakech — Underpriced, Guesthouse"
 *
 * Returns null when the page is not a property_detail or has no district.
 */
export function composePropertyH1(page: SeoPage, locale: Locale = DEFAULT_LOCALE): string | null {
  if (page.pageType !== "property_detail") return null;

  const district = getPropertyDistrict(page);
  if (!district) return null;

  const facts = (findSection(page, "facts")?.data ?? null) as Record<string, unknown> | null;
  const bestUseSection = (findSection(page, "best_use")?.data ?? null) as Record<string, unknown> | null;
  const pricingSection = (findSection(page, "pricing")?.data ?? null) as Record<string, unknown> | null;

  const sqm = pickNum(facts, "builtSurfaceM2", "built_surface_m2");
  const primaryUse = localizedEnum(
    locale,
    "best_use_values",
    pickStr(bestUseSection, "primary"),
  );
  const rawPositioning = pickStr(pricingSection, "positioning", "pricePositioning");
  const positioning = localizedEnum(locale, "positioning_values", rawPositioning);

  // Base: "[sqm]m² Riad for sale in [District], Marrakech"
  const base = translate(locale, "h1_templates.property_base", { district });
  const sqmPrefix = sqm != null ? `${sqm}m² ` : "";
  const head = sqmPrefix
    ? base.replace(/^riad/i, `${sqmPrefix}riad`)
    : base;

  // Suffix: "— [Positioning][, BestUse]"
  const suffixParts: string[] = [];
  if (positioning) suffixParts.push(positioning);
  if (primaryUse) suffixParts.push(primaryUse);

  if (suffixParts.length === 0) return head;
  return `${head} — ${suffixParts.join(translate(locale, "h1_templates.property_join"))}`;
}

/**
 * Compose the <title> tag string for a property page.
 * Appends the brand name so we keep brand identity in search snippets while
 * surfacing the investment signal early (before the em-dash truncation point).
 *
 * Returns null for non-property pages — callers fall back to `page.title`.
 */
export function composePropertyPageTitle(
  page: SeoPage,
  locale: Locale = DEFAULT_LOCALE,
): string | null {
  const h1 = composePropertyH1(page, locale);
  if (!h1) return null;

  // Optionally append asking price if available — keeps the title dense with
  // data that matters to the buyer without making it too long.
  const pricingSection = (findSection(page, "pricing")?.data ?? null) as Record<string, unknown> | null;
  const asking = pickNum(pricingSection, "askingPriceMad", "asking_price_mad", "askingPrice");
  const priceStr = asking != null ? ` · ${formatMad(asking, locale)}` : "";

  return `${h1}${priceStr} — RiadIntel`;
}
